import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Plus, FileText, Eye, Download, Pencil, CalendarCheck2, Send, XCircle, Copy, CheckSquare, FileSpreadsheet, RefreshCw, FileCheck, FileEdit, HelpCircle, Lock, Check, EyeOff } from 'lucide-react';
import Switch from '../components/Switch';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { useModal } from '../components/AppModal';
import useUserPreference from '../hooks/useUserPreference';
import useTrimestreDeTrabajo from '../hooks/useTrimestreDeTrabajo';
import useViewMode from '../hooks/useViewMode';
import useFieldLayout from '../hooks/useFieldLayout';
import VistaControles from '../components/VistaControles';
import AccionesSeleccion from '../components/AccionesSeleccion';
import CierreTrimestreModal from '../components/CierreTrimestreModal';
import DataTable from '../components/DataTable';

// Badge VeriFactu compacto para la vista tabla (misma semántica que el de las
// tarjetas). Solo se muestra si la factura ha entrado realmente en el flujo.
const VF_BADGE = {
  pendiente:            { bg: '#fde68a', fg: '#92400e', tip: 'VeriFactu: en cola',                   txt: 'VF…' },
  firmado:              { bg: '#fde68a', fg: '#92400e', tip: 'VeriFactu: enviando…',                 txt: 'VF…' },
  aceptado:             { bg: '#16a34a', fg: '#ffffff', tip: 'VeriFactu: aceptado por la AEAT',      txt: 'VF✓' },
  aceptado_con_errores: { bg: '#f59e0b', fg: '#ffffff', tip: 'VeriFactu: aceptado con errores',      txt: 'VF±' },
  rechazado:            { bg: '#dc2626', fg: '#ffffff', tip: 'VeriFactu: rechazado por la AEAT',     txt: 'VF✗' },
  error_red:            { bg: '#94a3b8', fg: '#ffffff', tip: 'VeriFactu: error de red, reintentando', txt: 'VF…' },
};

const Invoices = ({ proformaMode = false }) => {
  const { hasPermission, activeTenant } = useAuth();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [invoiceToCancel, setInvoiceToCancel] = useState(null);
  const [invoiceToDuplicate, setInvoiceToDuplicate] = useState(null);
  const [duplicateReplacements, setDuplicateReplacements] = useState([{ from: '', to: '' }]);
  const [duplicating, setDuplicating] = useState(false);
  const [emailDrafts, setEmailDrafts] = useState({});
  const [sendingEmailIds, setSendingEmailIds] = useState([]);
  const [sendDialog, setSendDialog] = useState(null);
  // Tras ~1 min esperando a VeriFactu se permite enviar igualmente (la factura
  // ya es válida con su QR; el registro en la AEAT se reintenta solo).
  const [sendForceAllowed, setSendForceAllowed] = useState(false);
  // Tope de ciclos del sondeo automático de VeriFactu (evita sondear sin fin).
  const vfPollCountRef = useRef(0);
  const navigate = useNavigate();
  const modal = useModal();
  const today = new Date();
  const currentQuarter = Math.floor(today.getMonth() / 3) + 1;
  const currentYear = today.getFullYear();
  const [filters, setFilters] = useUserPreference(`invoices:t${activeTenant?.id ?? 'default'}`, {
    search: '',
    cancelledFilter: '',
    collectionFilter: '',
    originFilter: '',
    typeFilter: '',
    visibleFilter: '',
    asesoriaFilter: '',
    closedFilter: '',
    accountedFilter: '',
    customerFilter: '',
    sortKey: 'date_desc',
  });
  const search = filters.search ?? '';
  const cancelledFilter = filters.cancelledFilter ?? '';
  const collectionFilter = filters.collectionFilter ?? '';
  const originFilter = filters.originFilter ?? '';
  // Salientes muestra SOLO facturas; el apartado Proformas, SOLO proformas.
  const typeFilter = proformaMode ? 'proformas' : 'facturas';
  // El tipo no es un filtro más que el usuario elige: es la pantalla en la que
  // está. Todo lo que cuente o seleccione documentos tiene que respetarlo, o
  // acaba contando (y seleccionando) los del otro tipo, que ni se listan aquí.
  const esDelTipoDeLaPantalla = (inv) => (typeFilter === 'facturas' && !inv.isProforma)
    || (typeFilter === 'proformas' && inv.isProforma === true);
  const visibleFilter = filters.visibleFilter ?? '';
  // La marca de asesoría no existe en Proformas: allí el filtro ni se ve ni
  // filtra, aunque la preferencia guardada sea la misma que la de Salientes.
  const asesoriaFilter = proformaMode ? '' : (filters.asesoriaFilter ?? '');
  const closedFilter = filters.closedFilter ?? '';
  // En Proformas no hay contabilización: igual que la marca de asesoría, allí
  // el filtro ni se ve ni filtra.
  const accountedFilter = proformaMode ? '' : (filters.accountedFilter ?? '');
  const customerFilter = filters.customerFilter ?? '';
  const sortKey = filters.sortKey ?? 'date_desc';
  const { year, quarter, setYear, setQuarter } = useTrimestreDeTrabajo('emitidas');
  const [selectedIds, setSelectedIds] = useState([]);
  const [viewMode, setViewMode] = useViewMode(proformaMode ? 'proformas' : 'invoices');
  const fl = useFieldLayout(proformaMode ? 'proformas' : 'invoices', viewMode);
  const [bulkRunning, setBulkRunning] = useState(false);
  // Cierre de trimestre en curso (modal «Cerrar trimestre»): qué se cierra y
  // en qué trimestre está cada grupo. null = modal cerrado.
  const [cierre, setCierre] = useState(null);
  const [requireProformaFirst, setRequireProformaFirst] = useState(false);
  const [proformaToggleSaving, setProformaToggleSaving] = useState(false);

  // Trimestre efectivo de la factura emitida:
  //  1. Si tiene el trimestre fijado (el que se eligió al cerrarla, cuando el
  //     día del cierre la habría colocado en otro) → ese trimestre.
  //  2. Si quarterClosedAt no nulo → trimestre anterior al de la fecha de cierre
  //     (queda clavado por el cierre manual del trimestre).
  //  3. Resto → trimestre real de la FECHA de la factura. Una factura vive
  //     siempre en el trimestre de su fecha; NO se reubica porque avance el
  //     calendario (una factura de junio es T2 para siempre).
  const getInvoiceQuarter = (inv) => {
    if (inv.quarterOverrideYear && inv.quarterOverrideQuarter) {
      return { year: inv.quarterOverrideYear, quarter: inv.quarterOverrideQuarter };
    }
    if (inv.quarterClosedAt) {
      const c = new Date(inv.quarterClosedAt);
      if (!isNaN(c.getTime())) {
        const cy = c.getFullYear();
        const cq = Math.floor(c.getMonth() / 3) + 1;
        return cq > 1 ? { year: cy, quarter: cq - 1 } : { year: cy - 1, quarter: 4 };
      }
    }
    const d = new Date(inv.date);
    if (isNaN(d.getTime())) return null;
    return { year: d.getFullYear(), quarter: Math.floor(d.getMonth() / 3) + 1 };
  };

  useEffect(() => { loadInvoices(); }, []);

  // Refresco automático mientras haya facturas con VeriFactu en curso (en cola,
  // enviando o reintentando): el badge pasa solo de VF… a VF✓ / VF✗ y, si hay un
  // envío al cliente esperando, se desbloquea en cuanto la AEAT acepta. Sin
  // recargar a mano. Tope ~4 min (40 × 6 s) para no sondear indefinidamente.
  useEffect(() => {
    const hayEnCurso = invoices.some((i) => i.verifactuQueued);
    if (!hayEnCurso) { vfPollCountRef.current = 0; return; }
    if (vfPollCountRef.current >= 40) return;
    const t = setTimeout(() => {
      vfPollCountRef.current += 1;
      loadInvoices();
    }, 6000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoices]);

  // Cronómetro del "enviar de todas formas": al abrir el diálogo de envío se
  // reinicia; si VeriFactu no resuelve en ~1 min, se habilita el envío manual.
  useEffect(() => {
    if (!sendDialog) { setSendForceAllowed(false); return; }
    setSendForceAllowed(false);
    const t = setTimeout(() => setSendForceAllowed(true), 60000);
    return () => clearTimeout(t);
    // Depende SOLO del id (no del objeto sendDialog): teclear en el email no
    // debe reiniciar el cronómetro del "enviar de todas formas".
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sendDialog?.invoice?.id]);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/settings');
        const proformaRow = (res.data || []).find((s) => s.key === 'require_proforma_first');
        setRequireProformaFirst(!!proformaRow && String(proformaRow.value).trim().toLowerCase() === 'true');
      } catch (e) {
        console.error('[facturas] no se pudo leer «Facturas solo desde proforma» de los ajustes', e);
      }
    })();
  }, []);

  const toggleRequireProformaFirst = async () => {
    const next = !requireProformaFirst;
    setProformaToggleSaving(true);
    try {
      await api.put('/settings/bulk', {
        settings: [{ key: 'require_proforma_first', value: next ? 'true' : 'false', group: 'invoicing' }],
      });
      setRequireProformaFirst(next);
    } catch (e) {
      modal.error('No se pudo cambiar «Facturas solo desde proforma»');
    } finally {
      setProformaToggleSaving(false);
    }
  };

  const loadInvoices = async () => {
    try {
      const res = await api.get('/invoices');
      setInvoices(res.data);
      const validIds = new Set(res.data.map(i => i.id));
      setSelectedIds(prev => prev.filter(id => validIds.has(id)));
      setEmailDrafts((prev) => {
        const next = { ...prev };
        res.data.forEach((invoice) => {
          if (next[invoice.id] === undefined) {
            next[invoice.id] = invoice.customer?.email || '';
          }
        });
        return next;
      });
    } catch (error) {
      modal.error('Error cargando facturas');
    } finally {
      setLoading(false);
    }
  };

  const handleCollectionChange = async (id, collectionStatus) => {
    try {
      const res = await api.patch(`/invoices/${id}/collection`, { collectionStatus });
      setInvoices(invoices.map(i => i.id === id ? { ...i, collectionStatus: res.data.collectionStatus, collectedAmount: res.data.collectedAmount } : i));
    } catch (error) {
      modal.error(error.response?.data?.message || 'Error actualizando cobro');
    }
  };

  const handleQuickFlag = async (id, field, value) => {
    try {
      const res = await api.patch(`/invoices/${id}/quick-flag`, { field, value });
      setInvoices(invoices.map(i => i.id === id ? { ...i, ...res.data } : i));
    } catch (error) {
      modal.error(error.response?.data?.message || 'Error actualizando estado');
    }
  };

  const toggleAccounted = async (id, value) => {
    try {
      const res = await api.patch(`/invoices/${id}/accounted`, { isAccounted: value });
      setInvoices(invoices.map(i => i.id === id ? { ...i, isAccounted: res.data.isAccounted } : i));
    } catch (error) {
      modal.error(error.response?.data?.message || 'Error actualizando contabilizada');
    }
  };

  const bulkApply = (label, ids, fn) => {
    if (ids.length === 0) return;
    modal.confirm(
      `¿${label} en ${ids.length} factura${ids.length === 1 ? '' : 's'} seleccionada${ids.length === 1 ? '' : 's'}?`,
      async () => {
        setBulkRunning(true);
        try {
          const results = await Promise.allSettled(ids.map(fn));
          const failed = results.filter(r => r.status === 'rejected').length;
          await loadInvoices();
          setSelectedIds([]);
          if (failed === 0) modal.success(`${label}: ${ids.length} aplicada${ids.length === 1 ? '' : 's'}.`);
          else modal.error(`${label}: ${ids.length - failed} aplicada${(ids.length - failed) === 1 ? '' : 's'}, ${failed} fallida${failed === 1 ? '' : 's'}.`);
        } finally {
          setBulkRunning(false);
        }
      }
    );
  };

  // Cerrar las marcadas: abre el modal «Cerrar trimestre» con una línea por
  // trimestre en el que están (desde la vista «Año» puede haber varios).
  const bulkCloseQuarter = () => {
    const porTrimestre = new Map();
    shownSelected.filter(inv => !inv.quarterClosedAt).forEach(inv => {
      const q = getInvoiceQuarter(inv);
      if (!q) return;
      const clave = `${q.year}-${q.quarter}`;
      if (!porTrimestre.has(clave)) porTrimestre.set(clave, { year: q.year, quarter: q.quarter, ids: [] });
      porTrimestre.get(clave).ids.push(inv.id);
    });
    const grupos = [...porTrimestre.values()]
      .sort((a, b) => (a.year - b.year) || (a.quarter - b.quarter))
      .map(g => ({ ...g, cuantas: g.ids.length }));
    if (grupos.length === 0) return;
    setCierre({ deMarcadas: true, grupos });
  };

  // Cierre del trimestre filtrado COMPLETO (todas las facturas de ese periodo),
  // de un golpe, sin tener que seleccionarlas. La reapertura va en Ajustes.
  const closeCurrentQuarter = () => {
    if (quarter === 'all' || closableCount === 0) return;
    setCierre({ deMarcadas: false, grupos: [{ year, quarter, cuantas: closableCount }] });
  };

  // Aceptado el modal «Cerrar trimestre»: cada grupo se cierra con el trimestre
  // elegido para él. Las marcadas van por sus ids; el trimestre completo, por
  // la fecha de las facturas, como siempre.
  const confirmarCierre = async (destinos) => {
    if (!cierre) return;
    const documento = (n) => (proformaMode ? (n === 1 ? 'proforma' : 'proformas') : (n === 1 ? 'factura' : 'facturas'));
    setBulkRunning(true);
    try {
      const partes = [];
      for (let i = 0; i < cierre.grupos.length; i += 1) {
        const g = cierre.grupos[i];
        const d = destinos[i];
        const res = cierre.deMarcadas
          ? await api.post('/invoices/bulk-close-quarter', { ids: g.ids, year: d.year, quarter: d.quarter })
          : await api.post('/invoices/close-quarter', { year: g.year, quarter: g.quarter, targetYear: d.year, targetQuarter: d.quarter });
        const hechas = cierre.deMarcadas ? res.data.count : res.data.closed;
        const proformas = res.data.closedProformas;
        partes.push(`${hechas} ${documento(hechas)}${proformas > 0 ? ` y ${proformas} ${proformas === 1 ? 'proforma' : 'proformas'}` : ''} en el T${d.quarter} de ${d.year}`);
      }
      setCierre(null);
      await loadInvoices();
      if (cierre.deMarcadas) setSelectedIds([]);
      modal.success(`Trimestre cerrado: ${partes.join('; ')}.`);
    } catch (e) {
      setCierre(null);
      await loadInvoices();
      modal.error(e.response?.data?.message || 'No se pudo cerrar el trimestre');
    } finally {
      setBulkRunning(false);
    }
  };

  const bulkMarkPaid = () => {
    const ids = idsSeleccionadosDelTrimestre(inv => inv.collectionStatus !== 'cobrada' && !inv.cancelledAt);
    return bulkApply('Marcar como cobradas', ids, (id) => api.patch(`/invoices/${id}/collection`, { collectionStatus: 'cobrada' }));
  };

  const bulkMarkSentCustomer = () => {
    const ids = idsSeleccionadosDelTrimestre(inv => !inv.sentToCustomerAt && !inv.cancelledAt);
    return bulkApply('Marcar como enviadas al cliente', ids, (id) => api.patch(`/invoices/${id}/quick-flag`, { field: 'sentToCustomerAt', value: new Date().toISOString() }));
  };

  // Contabilizada: se marca cuando la asesoría confirma que las ha contabilizado.
  // Se refleja en el portal de asesoría (estado "contabilizada").
  const bulkMarkAccounted = () => {
    const ids = idsSeleccionadosDelTrimestre(inv => !inv.isAccounted && !inv.cancelledAt);
    return bulkApply('Marcar como contabilizadas', ids, (id) => api.patch(`/invoices/${id}/accounted`, { isAccounted: true }));
  };

  // Quitar la marca de contabilizada (la asesoría se retracta), como en Entrantes.
  const bulkUnmarkAccounted = () => {
    const ids = idsSeleccionadosDelTrimestre(inv => inv.isAccounted && !inv.cancelledAt);
    return bulkApply('Quitar la marca de contabilizada', ids, (id) => api.patch(`/invoices/${id}/accounted`, { isAccounted: false }));
  };

  // «Visible asesoría»: marca propia de la factura emitida, independiente de
  // «Enviada al cliente»; es la que decide qué ve la asesoría en su portal.
  // Una proforma nunca se enseña (el servidor también lo rechaza).
  const handleToggleAsesoria = (inv) => {
    if (inv.isProforma) return;
    handleQuickFlag(inv.id, 'sentToAccountantAt', inv.sentToAccountantAt ? null : new Date().toISOString());
  };

  // Enseñar u ocultar a la asesoría las marcadas en una sola llamada, como en
  // Entrantes: el servidor dice cuántas ha cambiado y cuántas no.
  const bulkAsesoria = (visible) => {
    const ids = idsSeleccionadosDelTrimestre(inv => !inv.isProforma && Boolean(inv.sentToAccountantAt) !== visible);
    if (ids.length === 0) return;
    const label = visible ? 'Marcar como visibles para la asesoría' : 'Ocultar a la asesoría';
    modal.confirm(
      `¿${label} en ${ids.length} factura${ids.length === 1 ? '' : 's'} seleccionada${ids.length === 1 ? '' : 's'}?`,
      async () => {
        setBulkRunning(true);
        try {
          const res = await api.post('/invoices/bulk-asesoria', { ids, visible });
          await loadInvoices();
          setSelectedIds([]);
          modal.success(res.data?.message || `${label}: aplicado a ${ids.length} factura${ids.length === 1 ? '' : 's'}.`);
        } catch (error) {
          modal.error(error.response?.data?.message || `Error en operación: ${label}`);
        } finally {
          setBulkRunning(false);
        }
      }
    );
  };

  const handleCancelInvoice = async (id) => {
    try {
      const res = await api.post(`/invoices/${id}/cancel`);
      setInvoices(invoices.map(i => i.id === id ? { ...i, cancelledAt: res.data.cancelledAt } : i));
      setInvoiceToCancel(null);
    } catch (error) {
      modal.error(error.response?.data?.message || 'Error anulando factura');
    }
  };

  const handleViewPdf = async (invoiceId) => {
    try {
      const res = await api.get(`/invoices/${invoiceId}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      window.open(url, '_blank');
    } catch (error) {
      modal.error('Error abriendo PDF');
    }
  };

  const handleDownloadPdf = async (invoiceId) => {
    try {
      const res = await api.get(`/invoices/${invoiceId}/pdf/download`, { responseType: 'blob' });
      const disposition = res.headers['content-disposition'] || '';
      const match = disposition.match(/filename="?([^"]+)"?/);
      const fileName = match ? match[1] : `Factura_${invoiceId}.pdf`;
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      modal.error('Error descargando PDF');
    }
  };

  // La tarjeta lleva su casilla de email y exige que esté rellena; la tabla no
  // tiene casilla (desdeTabla): abre el diálogo con el email que haya —el del
  // cliente o el tecleado en la tarjeta— y el destinatario se escribe allí.
  const handleSendEmail = async (invoice, { desdeTabla = false } = {}) => {
    const email = String(emailDrafts[invoice.id] ?? '').trim();
    if (!email && !desdeTabla) {
      return modal.error('Indica un email destinatario para esta factura');
    }
    try {
      const res = await api.get(`/invoices/${invoice.id}/send-email/preview`);
      setSendDialog({
        invoice,
        recipient: email,
        sendCopy: true,
        preview: res.data,
      });
    } catch (error) {
      modal.error(error.response?.data?.message || 'Error preparando el envío');
    }
  };

  const handleConfirmSend = async () => {
    if (!sendDialog) return;
    const { invoice, recipient, sendCopy } = sendDialog;
    // Seguridad: nunca enviar al cliente una factura que la AEAT ha rechazado en
    // VeriFactu: se corrige lo que diga la AEAT y se reenvía («Reenviar a
    // VeriFactu» genera el registro nuevo con la marca de rechazo previo).
    const live = invoices.find((i) => i.id === invoice.id) || invoice;
    if (live.verifactuStatus === 'rechazado') {
      return modal.error('VeriFactu ha RECHAZADO esta factura: no se puede enviar al cliente. Corrige lo que indica Hacienda y pulsa «Reenviar a VeriFactu».');
    }
    setSendingEmailIds((prev) => [...prev, invoice.id]);
    try {
      const res = await api.post(`/invoices/${invoice.id}/send-email`, {
        email: recipient,
        sendCopyToSelf: sendCopy,
      });
      const sentAt = res.data?.sentToCustomerAt;
      if (sentAt) {
        setInvoices((prev) => prev.map((i) => i.id === invoice.id ? { ...i, sentToCustomerAt: sentAt } : i));
      }
      modal.success(res.data.message || `Factura enviada por email a ${recipient}`);
      setSendDialog(null);
    } catch (error) {
      const backendErrors = error.response?.data?.errors;
      const backendMessage = Array.isArray(backendErrors) && backendErrors[0]?.msg
        ? backendErrors[0].msg
        : error.response?.data?.message;
      modal.error(backendMessage || 'Error enviando la factura por email');
    } finally {
      setSendingEmailIds((prev) => prev.filter((itemId) => itemId !== invoice.id));
    }
  };

  const openDuplicateDialog = (invoice) => {
    setInvoiceToDuplicate(invoice);
    setDuplicateReplacements([{ from: '', to: '' }]);
  };

  const closeDuplicateDialog = () => {
    if (duplicating) return;
    setInvoiceToDuplicate(null);
    setDuplicateReplacements([{ from: '', to: '' }]);
  };

  const updateDuplicateReplacement = (index, field, value) => {
    setDuplicateReplacements(prev => prev.map((r, i) => i === index ? { ...r, [field]: value } : r));
  };

  const addDuplicateReplacement = () => {
    setDuplicateReplacements(prev => [...prev, { from: '', to: '' }]);
  };

  const removeDuplicateReplacement = (index) => {
    setDuplicateReplacements(prev => prev.length === 1 ? [{ from: '', to: '' }] : prev.filter((_, i) => i !== index));
  };

  // Convertir proforma → factura definitiva y encadenar el envío por email
  // con el diálogo de envío ya existente (destinatario precargado y editable).
  const handleConvertProforma = (invoice) => {
    const defaultDate = String(invoice.date || '').slice(0, 10);
    modal.prompt(
      `Convertir la proforma ${invoice.invoiceNumber} en factura definitiva: se creará una factura con número legal nuevo, quedará emitida (y se registrará en VeriFactu si el envío automático está activo) y la proforma quedará bloqueada. Confirma o cambia la fecha de la factura real (por defecto, la de la proforma):`,
      { inputType: 'date', defaultValue: defaultDate, label: 'Fecha de la factura real' },
      async (chosenDate) => {
        try {
          const res = await api.post(`/invoices/${invoice.id}/convert`, chosenDate ? { date: chosenDate } : {});
          const nueva = res.data;
          await loadInvoices();
          modal.success(`Proforma convertida: factura ${nueva.invoiceNumber} creada.`);
          try {
            const prev = await api.get(`/invoices/${nueva.id}/send-email/preview`);
            setSendDialog({
              invoice: nueva,
              recipient: prev.data?.customerEmail || '',
              sendCopy: true,
              preview: prev.data,
            });
          } catch {
            // Si no se pudo preparar el envío, la conversión ya está hecha:
            // el usuario puede enviarla desde la card de la factura nueva.
          }
        } catch (error) {
          modal.error(error.response?.data?.message || 'Error convirtiendo la proforma');
        }
      }
    );
  };

  const handleDuplicateInvoice = async () => {
    if (!invoiceToDuplicate) return;
    const replacements = duplicateReplacements.filter(r => r.from.trim() !== '');
    setDuplicating(true);
    try {
      const res = await api.post(`/invoices/${invoiceToDuplicate.id}/duplicate`, {
        line_replacements: replacements.length > 0 ? replacements : undefined,
      });
      modal.success(`Proforma creada: ${res.data.invoiceNumber}`);
      setInvoiceToDuplicate(null);
      setDuplicateReplacements([{ from: '', to: '' }]);
      navigate(`/invoices/edit/${res.data.id}`);
    } catch (error) {
      modal.error(error.response?.data?.message || 'Error duplicando factura');
    } finally {
      setDuplicating(false);
    }
  };

  const collectionLabels = { pendiente: 'Pendiente', parcial: 'En parte', cobrada: 'Cobrada' };

  const isOverdue = (invoice) => {
    if (invoice.cancelledAt) return false;
    if (invoice.collectionStatus === 'cobrada') return false;
    return new Date(invoice.dueDate) < new Date(new Date().toDateString());
  };

  const collectionBadgeClass = (invoice) => {
    if (invoice.cancelledAt) return 'badge-gray';
    if (invoice.collectionStatus === 'cobrada') return 'badge-success';
    if (invoice.collectionStatus === 'parcial') return 'badge-warning';
    return isOverdue(invoice) ? 'badge-danger' : 'badge-warning';
  };

  // Anular: apagado en las anuladas y en las proformas ya convertidas (el
  // servidor las rechaza: se anula la factura resultante).
  const anulacion = (invoice) => {
    if (invoice.cancelledAt) return { activo: false, title: 'Factura ya anulada' };
    if (invoice.isProforma && invoice.convertedAt) return { activo: false, title: 'Proforma ya convertida en factura: si procede, anula la factura resultante' };
    return { activo: true, title: 'Anular factura' };
  };

  // Rectificar: abre la rectificativa de esta factura (misma factura, mismo
  // cliente). Una factura anulada ya no existe: no se rectifica.
  const rectificacion = (invoice) => (invoice.cancelledAt
    ? { activo: false, title: 'Una factura anulada no se rectifica' }
    : { activo: true, title: 'Hacer una factura rectificativa de esta factura' });

  // Reenviar a VeriFactu: solo con las facturas que la AEAT ha rechazado y que
  // no tienen ya un envío en curso. Los fallos de red los reintenta solo el
  // envío automático, y las facturas anteriores a VeriFactu no se mandan.
  const reenvioVerifactu = (invoice) => {
    if (invoice.isProforma) return { activo: false, title: 'Las proformas no van a VeriFactu' };
    if (['aceptado', 'aceptado_con_errores'].includes(invoice.verifactuStatus)) return { activo: false, title: 'Ya aceptada por la AEAT' };
    if (invoice.verifactuQueued) return { activo: false, title: 'Envío a VeriFactu en curso' };
    if (invoice.verifactuStatus !== 'rechazado') return { activo: false, title: 'Solo se reenvían las facturas rechazadas por la AEAT' };
    return { activo: true, title: 'Reenviar a VeriFactu (AEAT)' };
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(amount || 0);
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const formatDateTime = (date) => {
    return new Date(date).toLocaleString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const filtered = invoices.filter(inv => {
    const matchSearch = String(inv.invoiceNumberSeq).includes(search) ||
      inv.customer?.name?.toLowerCase().includes(search.toLowerCase()) ||
      inv.customer?.nif?.toLowerCase().includes(search.toLowerCase());
    const matchCancelled = !cancelledFilter
      || (cancelledFilter === 'anuladas' && !!inv.cancelledAt)
      || (cancelledFilter === 'vigentes' && !inv.cancelledAt);
    const matchCollection = !collectionFilter
      || (collectionFilter === 'vencida' && isOverdue(inv))
      || (collectionFilter !== 'vencida' && inv.collectionStatus === collectionFilter);
    const matchOrigin = !originFilter
      || (originFilter === 'minerva' && inv.createdByMinerva === true)
      || (originFilter === 'manual' && inv.createdByMinerva !== true);
    const matchType = !typeFilter
      || (typeFilter === 'facturas' && !inv.isProforma)
      || (typeFilter === 'proformas' && inv.isProforma === true);
    const matchVisible = !visibleFilter
      || (visibleFilter === 'si' && !!inv.sentToCustomerAt)
      || (visibleFilter === 'no' && !inv.sentToCustomerAt);
    const matchAsesoria = !asesoriaFilter
      || (asesoriaFilter === 'si' && !!inv.sentToAccountantAt)
      || (asesoriaFilter === 'no' && !inv.sentToAccountantAt);
    const matchClosed = !closedFilter
      || (closedFilter === 'si' && !!inv.quarterClosedAt)
      || (closedFilter === 'no' && !inv.quarterClosedAt);
    const matchAccounted = !accountedFilter
      || (accountedFilter === 'si' && !!inv.isAccounted)
      || (accountedFilter === 'no' && !inv.isAccounted);
    const matchCustomer = !customerFilter || String(inv.customer?.id ?? '') === String(customerFilter);
    const q = getInvoiceQuarter(inv);
    const matchQuarter = q && q.year === year && (quarter === 'all' || q.quarter === quarter);
    return matchSearch && matchCancelled && matchCollection && matchOrigin && matchType && matchVisible && matchAsesoria && matchClosed && matchAccounted && matchCustomer && matchQuarter;
  });

  const sortedFiltered = (() => {
    const arr = [...filtered];
    const dt = (it) => new Date(it.date).getTime() || 0;
    const cmp = (a, b) => {
      switch (sortKey) {
        case 'date_asc': return dt(a) - dt(b);
        case 'date_desc': return dt(b) - dt(a);
        case 'customer_asc': return (a.customer?.name || '').localeCompare(b.customer?.name || '', 'es');
        case 'customer_desc': return (b.customer?.name || '').localeCompare(a.customer?.name || '', 'es');
        case 'amount_asc': return (parseFloat(a.total) || 0) - (parseFloat(b.total) || 0);
        case 'amount_desc': return (parseFloat(b.total) || 0) - (parseFloat(a.total) || 0);
        case 'number_asc': return (a.invoiceNumberSeq || 0) - (b.invoiceNumberSeq || 0);
        case 'number_desc': return (b.invoiceNumberSeq || 0) - (a.invoiceNumberSeq || 0);
        default: return 0;
      }
    };
    return arr.sort(cmp);
  })();

  const activeFilterCount = [
    search ? 1 : 0,
    cancelledFilter ? 1 : 0,
    collectionFilter ? 1 : 0,
    originFilter ? 1 : 0,
    visibleFilter ? 1 : 0,
    asesoriaFilter ? 1 : 0,
    closedFilter ? 1 : 0,
    accountedFilter ? 1 : 0,
    customerFilter ? 1 : 0,
  ].reduce((a, b) => a + b, 0);

  const exportCsv = () => {
    const header = ['N.º', 'Fecha', 'Vencimiento', 'Cliente', 'Base imponible', 'IVA', 'IRPF', 'Total', 'Cobro', 'Enviada cliente', 'Trimestre cerrado'];
    const num = (n) => (parseFloat(n) || 0).toFixed(2).replace('.', ',');
    // El CSV exporta lo que la pantalla está mostrando: en Salientes, facturas;
    // en Proformas, proformas. sortedFiltered ya viene acotado al tipo de la
    // pantalla y a los filtros activos. Antes descartaba siempre las proformas
    // («el CSV es entrega fiscal»), así que desde Proformas salía vacío.
    const rows = sortedFiltered.map(inv => [
      inv.invoiceNumber || '',
      (inv.date || '').slice(0, 10),
      (inv.dueDate || '').slice(0, 10),
      (inv.customer?.name || '').replace(/"/g, '""'),
      num(inv.subtotal),
      num(inv.taxAmount),
      num(inv.irpfAmount),
      num(inv.total),
      inv.cancelledAt ? 'anulada' : (inv.collectionStatus || ''),
      inv.sentToCustomerAt ? 'sí' : 'no',
      inv.quarterClosedAt ? 'sí' : 'no',
    ]);
    const csv = '﻿' + [header, ...rows].map(r => r.map(c => `"${c}"`).join(';')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${proformaMode ? 'proformas' : 'facturas_emitidas'}_${year}_${quarter === 'all' ? 'anual' : 'T' + quarter}_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };
  const selectedSet = new Set(selectedIds);
  const selectableFilteredIds = sortedFiltered.map(inv => inv.id);
  const allFilteredSelected = selectableFilteredIds.length > 0
    && selectableFilteredIds.every(id => selectedSet.has(id));
  // Ids del TRIMESTRE actual (por getInvoiceQuarter, sin sub-filtros): lo que
  // selecciona "Marcar todas del trimestre", aparezcan o no en el filtro.
  // El TIPO sí se respeta: sin esa condición, en Proformas se contaban y se
  // seleccionaban también las facturas del trimestre, que ni siquiera se listan
  // ahí, y las acciones en masa caían sobre facturas fiscales fuera de la vista.
  const idsDelTrimestre = invoices
    .filter(inv => {
      if (!esDelTipoDeLaPantalla(inv)) return false;
      const q = getInvoiceQuarter(inv);
      return q && q.year === year && (quarter === 'all' || q.quarter === quarter);
    })
    .map(inv => inv.id);
  const trimestreIdSet = new Set(idsDelTrimestre);
  const allTrimestreSelected = idsDelTrimestre.length > 0 && idsDelTrimestre.every(id => selectedSet.has(id));
  // Contadores y acciones bulk operan SOLO sobre la selección del TRIMESTRE
  // actual (no sobre `invoices` global): excluye selecciones "fantasma" de otros
  // trimestres pero incluye las del trimestre que un sub-filtro oculte.
  const shownSelected = invoices.filter(inv => selectedSet.has(inv.id) && trimestreIdSet.has(inv.id));
  const selectedShownCount = shownSelected.length;
  const idsSeleccionadosDelTrimestre = (pred) => shownSelected.filter(pred).map(inv => inv.id);
  const toggleSelectAllFiltered = () => {
    if (selectableFilteredIds.length === 0) return;
    setSelectedIds(prev => {
      const prevSet = new Set(prev);
      const areAllSelected = selectableFilteredIds.every(id => prevSet.has(id));
      if (areAllSelected) return prev.filter(id => !selectableFilteredIds.includes(id));
      return [...new Set([...prev, ...selectableFilteredIds])];
    });
  };
  // Selecciona/deselecciona TODAS las facturas del trimestre actual (por su
  // fecha/trimestre), aparezcan o no en el filtro visible. Solo selecciona.
  const toggleSelectAllTrimestre = () => {
    if (idsDelTrimestre.length === 0) return;
    setSelectedIds(prev => {
      const prevSet = new Set(prev);
      const areAll = idsDelTrimestre.every(id => prevSet.has(id));
      if (areAll) return prev.filter(id => !trimestreIdSet.has(id));
      return [...new Set([...prev, ...idsDelTrimestre])];
    });
  };
  const toggleSelectOne = (id, checked) => {
    setSelectedIds(prev => checked ? [...new Set([...prev, id])] : prev.filter(x => x !== id));
  };

  const selectedNotClosedCount = shownSelected.filter(inv => !inv.quarterClosedAt).length;
  const selectedNotPaidCount = shownSelected.filter(inv => inv.collectionStatus !== 'cobrada' && !inv.cancelledAt).length;
  const selectedNotSentCount = shownSelected.filter(inv => !inv.sentToCustomerAt && !inv.cancelledAt).length;
  const selectedNotAccountedCount = shownSelected.filter(inv => !inv.isAccounted && !inv.cancelledAt).length;
  const selectedAccountedCount = shownSelected.filter(inv => inv.isAccounted && !inv.cancelledAt).length;
  // Visible asesoría: solo facturas (una proforma nunca se enseña).
  const selectedNotAsesoriaCount = shownSelected.filter(inv => !inv.isProforma && !inv.sentToAccountantAt).length;
  const selectedAsesoriaCount = shownSelected.filter(inv => !inv.isProforma && inv.sentToAccountantAt).length;

  // HSL determinista por bitmask de estados (B2)
  const stateBitmask = (inv) => {
    let m = 0;
    if (inv.sentToCustomerAt) m |= 1;
    if (inv.quarterClosedAt) m |= 2;
    if (inv.collectionStatus === 'cobrada') m |= 4;
    if (inv.cancelledAt) m |= 8;
    // Quién la hizo (Minerva o a mano) no entra en el color: lo dice el campo
    // «Origen».
    return m;
  };
  const cardBgFromState = (inv) => {
    const m = stateBitmask(inv);
    if (m === 0) return '#ffffff';
    const hue = (m * 47 + 137) % 360;
    // Sat 70 % / Light 86 % — antes 60/95 dejaba todos los colores casi
    // blancos y costaba diferenciarlos a primera vista. Ahora cada
    // combinación de estados se distingue claramente sin caer en
    // saturación chillona.
    return `hsl(${hue}, 70%, 86%)`;
  };
  const cardBorderFromState = (inv) => {
    const m = stateBitmask(inv);
    if (m === 0) return '#e2e8f0';
    const hue = (m * 47 + 137) % 360;
    return `hsl(${hue}, 60%, 62%)`;
  };
  // En la tabla, cada factura lleva el color de su tarjeta (clase dt-estado de
  // index.css); la que no tiene ningún estado se queda con la cebra.
  const filaConColor = (inv) => (stateBitmask(inv) === 0 ? {} : {
    className: 'dt-estado',
    style: { '--dt-estado-fondo': cardBgFromState(inv) },
  });

  // Leyenda dinámica de colores para el pie del listado: el fondo de cada
  // factura codifica la COMBINACIÓN de estados (no un color por estado), así
  // que la leyenda solo lista los colores que REALMENTE aparecen en la lista
  // filtrada, cada uno con la combinación que representa. Reutiliza las
  // mismas funciones de color que las tarjetas, por lo que siempre coincide.
  const stateFlagLabels = [
    { bit: 1, label: 'Enviada' },
    { bit: 2, label: 'Trim. cerrado' },
    { bit: 4, label: 'Cobrada' },
    { bit: 8, label: 'Anulada' },
  ];
  const colorLegend = (() => {
    const byMask = new Map();
    sortedFiltered.forEach(inv => {
      const m = stateBitmask(inv);
      if (!byMask.has(m)) byMask.set(m, inv);
    });
    return [...byMask.entries()]
      .sort((a, b) => a[0] - b[0])
      .map(([m, inv]) => ({
        labels: m === 0 ? 'Sin marcar' : stateFlagLabels.filter(f => m & f.bit).map(f => f.label).join(' + '),
        bg: cardBgFromState(inv),
        border: cardBorderFromState(inv),
      }));
  })();

  const totals = sortedFiltered.reduce((acc, inv) => {
    // En "Facturas" las proformas no cuentan (son totales fiscales). En la
    // pantalla de "Proformas" sí se suman las proformas mostradas (resumen
    // informativo, no fiscal); sin esto la fila de totales saldría a cero.
    if (!proformaMode && inv.isProforma) return acc;
    acc.base += parseFloat(inv.subtotal) || 0;
    acc.iva += parseFloat(inv.taxAmount) || 0;
    acc.irpf += parseFloat(inv.irpfAmount) || 0;
    acc.total += parseFloat(inv.total) || 0;
    return acc;
  }, { base: 0, iva: 0, irpf: 0, total: 0 });

  // Conteos por trimestre del año seleccionado (no aplica filtros adicionales,
  // pero sí el tipo de la pantalla: en Proformas cuenta proformas).
  const quarterCounts = [1, 2, 3, 4].map(q => invoices.filter(inv => {
    if (!esDelTipoDeLaPantalla(inv)) return false;
    const qi = getInvoiceQuarter(inv);
    return qi && qi.year === year && qi.quarter === q;
  }).length);
  // "N en total" del subtítulo: documentos del tipo de esta pantalla, de
  // cualquier año. Antes contaba también los del otro tipo.
  const totalDelTipo = invoices.filter(esDelTipoDeLaPantalla).length;

  // Facturas reales del trimestre filtrado aún sin cerrar (por fecha natural):
  // es lo que cerraría el botón "Cerrar Tn". Si es 0, no se muestra el botón.
  const closableCount = quarter === 'all' ? 0 : invoices.filter(inv => {
    if (inv.isProforma || inv.cancelledAt || inv.quarterClosedAt) return false;
    const d = new Date(inv.date);
    return d.getFullYear() === year && (Math.floor(d.getMonth() / 3) + 1) === quarter;
  }).length;

  // Conjunto de años con facturas (para acotar la navegación).
  const availableYears = Array.from(new Set(
    invoices.map(inv => getInvoiceQuarter(inv)?.year).filter(Boolean)
  )).sort((a, b) => a - b);
  const minYear = availableYears.length > 0 ? Math.min(...availableYears, currentYear) : currentYear;
  const maxYear = availableYears.length > 0 ? Math.max(...availableYears, currentYear) : currentYear;

  // Columnas de la vista tabla. Reutilizan los mismos handlers que las tarjetas
  // (cobro, envío, anular, VeriFactu, convertir). El envío por email con su
  // campo de destinatario se mantiene solo en la vista tarjetas.
  const invoiceColumns = [
    { key: 'select', header: '', render: (inv) => (
      <input
        type="checkbox"
        checked={selectedSet.has(inv.id)}
        onChange={e => toggleSelectOne(inv.id, e.target.checked)}
        title="Seleccionar factura"
      />
    ) },
    { key: 'num', header: 'Nº', className: 'dt-nowrap', fixed: true, render: (inv) => {
      const v = inv.verifactuStatus;
      const enFlujo = !!inv.verifactuSentAt || inv.verifactuQueued || ['aceptado', 'aceptado_con_errores', 'rechazado', 'error_red', 'firmado'].includes(v);
      const vf = enFlujo ? VF_BADGE[v] : null;
      return (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <strong>#{inv.invoiceNumberSeq}</strong>
          {inv.isProforma && (
            <span title={inv.convertedAt ? 'Proforma ya convertida en factura' : 'Proforma: sin validez fiscal'} style={{ padding: '1px 5px', borderRadius: 4, background: inv.convertedAt ? '#94a3b8' : '#2563eb', color: '#fff', fontSize: 10, fontWeight: 700 }}>{inv.convertedAt ? 'PRO→F' : 'PROFORMA'}</span>
          )}
          {inv.isRectificativa && (
            <span title={`Factura rectificativa ${inv.invoiceNumber}`} style={{ padding: '1px 5px', borderRadius: 4, background: 'var(--warning)', color: 'var(--secondary)', fontSize: 10, fontWeight: 700 }}>RECTIF.</span>
          )}
          {inv.createdByMinerva && (
            <span title="Creada por Minerva" style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 18, height: 18, borderRadius: '50%', background: '#7c3aed', color: '#fff', fontSize: 11, fontWeight: 700 }}>M</span>
          )}
          {vf && (
            <span title={vf.tip} style={{ padding: '1px 5px', borderRadius: 4, background: vf.bg, color: vf.fg, fontSize: 10, fontWeight: 700 }}>{vf.txt}</span>
          )}
        </span>
      );
    } },
    { key: 'customer', header: 'Cliente', render: (inv) => (
      inv.customer ? <><span className="dt-sub">#{inv.customer.id}</span> {inv.customer.name}</> : '—'
    ) },
    { key: 'date', header: 'Emisión', className: 'dt-nowrap', render: (inv) => formatDate(inv.date) },
    { key: 'dueDate', header: 'Vencim.', className: 'dt-nowrap', render: (inv) => (
      <span style={{ color: isOverdue(inv) ? '#dc2626' : 'inherit', fontWeight: isOverdue(inv) ? 600 : 400 }}>{formatDate(inv.dueDate)}</span>
    ) },
    { key: 'origin', header: 'Origen', render: (inv) => (inv.createdByMinerva ? 'Minerva' : 'A mano') },
    { key: 'subtotal', header: 'Base', className: 'num', render: (inv) => formatCurrency(parseFloat(inv.subtotal)) },
    { key: 'total', header: 'Total', className: 'num', render: (inv) => formatCurrency(parseFloat(inv.total)) },
    { key: 'collection', header: 'Cobro', render: (inv) => inv.cancelledAt ? (
      <span className="badge badge-danger" style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><XCircle size={12} /> Anulada</span>
    ) : (
      <select
        className={`badge ${collectionBadgeClass(inv)}`}
        value={inv.collectionStatus || 'pendiente'}
        onChange={e => handleCollectionChange(inv.id, e.target.value)}
        style={{ border: 'none', cursor: 'pointer', fontSize: '0.75rem', fontWeight: 600 }}
        title={isOverdue(inv) ? 'Vencida' : ''}
      >
        {['pendiente', 'parcial', 'cobrada'].map(c => (
          <option key={c} value={c}>{collectionLabels[c]}</option>
        ))}
      </select>
    ) },
    { key: 'states', header: 'Estado', render: (inv) => (
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
        {inv.sentToCustomerAt ? (
          <span className="badge badge-success" onClick={() => hasPermission('invoices', 'edit') && handleQuickFlag(inv.id, 'sentToCustomerAt', null)} title={`Enviada al cliente el ${formatDateTime(inv.sentToCustomerAt)} · clic para desmarcar`} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}><Send size={12} /> Enviada</span>
        ) : (
          <span className="badge badge-gray" onClick={() => hasPermission('invoices', 'edit') && handleQuickFlag(inv.id, 'sentToCustomerAt', new Date().toISOString())} title="No enviada al cliente · clic para marcar como enviada" style={{ display: 'inline-flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}><Send size={12} /> Sin enviar</span>
        )}
        {inv.quarterClosedAt ? (
          <span className="badge badge-info" title={`Trimestre cerrado el ${formatDateTime(inv.quarterClosedAt)}`} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><CalendarCheck2 size={12} /> Trim. cerrado</span>
        ) : (
          <span className="badge badge-gray" title="Trimestre abierto" style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><CalendarCheck2 size={12} /> Trim. abierto</span>
        )}
        {!inv.isProforma && (inv.isAccounted ? (
          <span className="badge badge-info" onClick={() => hasPermission('invoices', 'edit') && !inv.cancelledAt && toggleAccounted(inv.id, false)} title="Contabilizada (confirmada por la asesoría) · clic para desmarcar" style={{ display: 'inline-flex', alignItems: 'center', gap: 4, cursor: inv.cancelledAt ? 'default' : 'pointer' }}><FileCheck size={12} /> Contabilizada</span>
        ) : (
          <span className="badge badge-gray" onClick={() => hasPermission('invoices', 'edit') && !inv.cancelledAt && toggleAccounted(inv.id, true)} title="Sin contabilizar · clic para marcar como contabilizada" style={{ display: 'inline-flex', alignItems: 'center', gap: 4, cursor: inv.cancelledAt ? 'default' : 'pointer' }}><FileCheck size={12} /> Sin contabilizar</span>
        ))}
        {!inv.isProforma && (inv.sentToAccountantAt ? (
          <span className="badge badge-success" onClick={() => hasPermission('invoices', 'edit') && handleToggleAsesoria(inv)} title={`Visible para la asesoría desde el ${formatDateTime(inv.sentToAccountantAt)} · clic para ocultar`} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, cursor: hasPermission('invoices', 'edit') ? 'pointer' : 'default' }}><Check size={12} /> Visible asesoría</span>
        ) : (
          <span className="badge badge-gray" onClick={() => hasPermission('invoices', 'edit') && handleToggleAsesoria(inv)} title="No visible para la asesoría · clic para hacer visible" style={{ display: 'inline-flex', alignItems: 'center', gap: 4, cursor: hasPermission('invoices', 'edit') ? 'pointer' : 'default' }}><Check size={12} /> No visible</span>
        ))}
      </div>
    ) },
    { key: 'actions', header: '', className: 'col-actions', render: (inv) => (
      <div className="dt-actions">
        <button className="btn-icon" onClick={() => handleViewPdf(inv.id)} title="Ver PDF"><Eye size={16} /></button>
        <button className="btn-icon" onClick={() => handleDownloadPdf(inv.id)} title="Descargar PDF"><Download size={16} /></button>
        <button type="button" className="btn-icon" onClick={() => handleSendEmail(inv, { desdeTabla: true })} disabled={sendingEmailIds.includes(inv.id)} title={sendingEmailIds.includes(inv.id) ? 'Enviando...' : 'Enviar por email'}><Send size={16} /></button>
        <button className="btn-icon" onClick={() => navigate(`/invoices/edit/${inv.id}`)} title="Editar"><Pencil size={16} /></button>
        {proformaMode && hasPermission('invoices', 'create') && (
          <button type="button" className="btn-icon" disabled={Boolean(inv.convertedAt) || Boolean(inv.cancelledAt)} onClick={() => handleConvertProforma(inv)} title={inv.convertedAt ? 'Proforma ya convertida' : inv.cancelledAt ? 'Proforma anulada' : 'Convertir en factura'} style={{ color: (inv.convertedAt || inv.cancelledAt) ? undefined : '#2563eb' }}><FileCheck size={16} /></button>
        )}
        {hasPermission('invoices', 'create') && (
          <button className="btn-icon" onClick={() => openDuplicateDialog(inv)} title="Duplicar factura"><Copy size={16} /></button>
        )}
        {hasPermission('invoices', 'edit') && (
          <button type="button" className="btn-icon" disabled={!anulacion(inv).activo} onClick={() => setInvoiceToCancel(inv.id)} title={anulacion(inv).title} style={{ color: anulacion(inv).activo ? '#b45309' : undefined }}><XCircle size={16} /></button>
        )}
        {hasPermission('invoices', 'edit') && (
          <button
            type="button"
            className="btn-icon"
            disabled={!reenvioVerifactu(inv).activo}
            onClick={async () => {
              try {
                await api.post(`/invoices/${inv.id}/verifactu/retry`);
                await loadInvoices();
                modal.success('Encolado a VeriFactu. En 30 s aparece el resultado en el badge.');
              } catch (e) {
                modal.error(e.response?.data?.message || 'No se pudo encolar a VeriFactu');
              }
            }}
            title={reenvioVerifactu(inv).title}
            style={{ color: reenvioVerifactu(inv).activo ? '#1d4ed8' : undefined }}
          ><RefreshCw size={16} /></button>
        )}
        {!proformaMode && hasPermission('invoices', 'create') && (
          <button type="button" className="btn-icon" disabled={!rectificacion(inv).activo} onClick={() => navigate(`/invoices/new?rectifica=${inv.id}`)} title={rectificacion(inv).title}><FileEdit size={16} /></button>
        )}
      </div>
    ) },
  ];

  const fields = fl.fields(invoiceColumns);

  // Menú «Acciones», junto a las casillas de marcar todas: lo que se puede
  // hacer a la vez con las marcadas. Recoge los botones que antes iban sueltos
  // en esa fila (solo salían con algo marcado) y suma enseñar u ocultar a la
  // asesoría. La asesoría y contabilizar no aplican en Proformas. Cada rótulo
  // dice la acción con el mismo nombre que su confirmación, nunca el estado en
  // que quedan.
  const accionesSeleccion = [
    ...(proformaMode ? [] : [
      { key: 'asesoria-si', label: `Marcar como visibles para la asesoría (${selectedNotAsesoriaCount})`, icon: Check, disabled: selectedNotAsesoriaCount === 0, onClick: () => bulkAsesoria(true), title: 'Marcar las seleccionadas como visibles para la asesoría' },
      { key: 'asesoria-no', label: `Ocultar a la asesoría (${selectedAsesoriaCount})`, icon: EyeOff, disabled: selectedAsesoriaCount === 0, onClick: () => bulkAsesoria(false), title: 'Ocultar las seleccionadas a la asesoría' },
    ]),
    { key: 'cerrar', label: `Cerrar trimestre (${selectedNotClosedCount})`, icon: CalendarCheck2, disabled: selectedNotClosedCount === 0, onClick: bulkCloseQuarter, title: 'Marca el trimestre como cerrado en las facturas seleccionadas que aún no lo estén' },
    { key: 'cobradas', label: `Marcar como cobradas (${selectedNotPaidCount})`, icon: CheckSquare, disabled: selectedNotPaidCount === 0, onClick: bulkMarkPaid, title: 'Marca como cobradas las facturas seleccionadas vigentes que no lo estén' },
    { key: 'enviadas', label: `Marcar como enviadas al cliente (${selectedNotSentCount})`, icon: Send, disabled: selectedNotSentCount === 0, onClick: bulkMarkSentCustomer, title: 'Marca como enviadas al cliente las seleccionadas vigentes que no lo estén' },
    ...(proformaMode ? [] : [
      { key: 'contabilizadas', label: `Marcar como contabilizadas (${selectedNotAccountedCount})`, icon: FileCheck, disabled: selectedNotAccountedCount === 0, onClick: bulkMarkAccounted, title: 'Marca como contabilizadas las seleccionadas que no lo estén (cuando la asesoría lo confirma). Se refleja en el portal de asesoría.' },
      { key: 'quitar-contab', label: `Quitar la marca de contabilizada (${selectedAccountedCount})`, icon: XCircle, disabled: selectedAccountedCount === 0, onClick: bulkUnmarkAccounted, title: 'Quita la marca de contabilizada en las seleccionadas (cuando la asesoría se retracta). Se refleja en el portal de asesoría.' },
    ]),
  ];

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">{proformaMode ? 'Proformas' : 'Facturas'}</h1>
          <p className="page-subtitle">Mostrando {quarter === 'all' ? 'año' : `T${quarter}`} {year} · {sortedFiltered.length} {proformaMode ? 'proformas' : 'facturas'} · {totalDelTipo} en total</p>
        </div>
        {!proformaMode && hasPermission('invoices', 'create') && (
          <button
            className="btn btn-primary"
            onClick={() => requireProformaFirst
              ? modal.info('Está activado «Facturas solo desde proforma». Crea primero una proforma con «Nueva proforma» y, cuando esté revisada, conviértela en factura.')
              : navigate('/invoices/new')}
            style={requireProformaFirst ? { opacity: 0.55 } : undefined}
            title={requireProformaFirst ? 'Bloqueado: «Facturas solo desde proforma» está activo. Crea una proforma y conviértela.' : undefined}
          >
            <Plus size={18} /> Nueva factura
          </button>
        )}
        {hasPermission('invoices', 'create') && (
          <button
            className="btn btn-outline"
            onClick={() => navigate('/invoices/new?proforma=1')}
            style={{ marginLeft: 8 }}
            title="Crea una proforma: numeración independiente, editable y sin validez fiscal. Se convierte en factura cuando el cliente la apruebe."
          >
            <Plus size={18} /> Nueva proforma
          </button>
        )}
        <button
          className="btn btn-outline"
          onClick={exportCsv}
          style={{ marginLeft: 8 }}
          title="Exporta a CSV el listado filtrado actual."
          disabled={sortedFiltered.length === 0}
        >
          <FileSpreadsheet size={18} /> Exportar CSV
        </button>
      </div>

      <div className="card" style={{ marginBottom: 12, position: 'sticky', top: 0, zIndex: 5 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <button
              type="button"
              className="btn-icon"
              onClick={() => setYear(year - 1)}
              disabled={year <= minYear}
              title="Año anterior"
              style={{ padding: '6px 10px' }}
            >◀</button>
            <span style={{ fontWeight: 700, fontSize: 16, minWidth: 56, textAlign: 'center' }}>{year}</span>
            <button
              type="button"
              className="btn-icon"
              onClick={() => setYear(year + 1)}
              disabled={year >= maxYear}
              title="Año siguiente"
              style={{ padding: '6px 10px' }}
            >▶</button>
          </div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {[1, 2, 3, 4].map(q => {
              const isActive = q === quarter;
              const count = quarterCounts[q - 1];
              const isCurrentRealQuarter = year === currentYear && q === currentQuarter;
              return (
                <button
                  key={q}
                  type="button"
                  onClick={() => setQuarter(q)}
                  className={isActive ? 'btn btn-primary' : 'btn btn-outline'}
                  style={{ padding: '6px 14px' }}
                  title={isCurrentRealQuarter ? 'Trimestre actual' : `Trimestre ${q}`}
                >
                  T{q}{isCurrentRealQuarter ? ' ·' : ''} <span style={{ opacity: 0.7, marginLeft: 4 }}>({count})</span>
                </button>
              );
            })}
            <button
              type="button"
              onClick={() => setQuarter('all')}
              className={quarter === 'all' ? 'btn btn-primary' : 'btn btn-outline'}
              style={{ padding: '6px 14px' }}
              title="Todo el año"
            >
              Año <span style={{ opacity: 0.7, marginLeft: 4 }}>({quarterCounts.reduce((a, b) => a + b, 0)})</span>
            </button>
            {!proformaMode && quarter !== 'all' && closableCount > 0 && (
              <button
                type="button"
                onClick={closeCurrentQuarter}
                className="btn"
                style={{ padding: '6px 14px', marginLeft: 12, background: '#d97706', color: '#fff', border: 'none', display: 'inline-flex', alignItems: 'center', gap: 6, fontWeight: 600, boxShadow: '0 1px 3px rgba(217,119,6,0.4)' }}
                title={`Cerrar el trimestre T${quarter} de ${year}: marca como cerradas las ${closableCount} factura(s) de ese periodo y sus proformas. Solo se revierte desde Ajustes.`}
              >
                <Lock size={14} /> Cerrar T{quarter} ({closableCount})
              </button>
            )}
          </div>
          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
              <span
                title="Facturas solo desde proforma: si está encendido, hay que crear primero una proforma y convertirla; la «Nueva factura» directa queda bloqueada."
                style={{ display: 'inline-flex', alignItems: 'center', color: requireProformaFirst ? 'var(--success)' : '#94a3b8', cursor: 'help' }}
              >
                <FileCheck size={20} />
              </span>
              <Switch
                checked={requireProformaFirst}
                disabled={proformaToggleSaving}
                onChange={toggleRequireProformaFirst}
                label="Facturas solo desde proforma"
              />
              <button
                type="button"
                aria-label="Qué es «Facturas solo desde proforma»"
                onClick={() => modal.info('Cuando está activo, no se puede crear una factura directa: primero se crea una proforma y, cuando esté revisada, se convierte en factura. Las rectificativas y la conversión de una proforma siguen permitidas.')}
                style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', display: 'flex', alignItems: 'center', color: '#94a3b8' }}
              >
                <HelpCircle size={15} />
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="filter-bar">
          <div className="filter-field filter-field--search">
            <label className="filter-label">Buscar</label>
            <div className="search-input-wrapper" style={search ? { boxShadow: '0 0 0 2px var(--primary)', borderRadius: 6 } : undefined}>
              <Search />
              <input
                type="text"
                className="form-input"
                placeholder="Nº, cliente o NIF..."
                value={search}
                onChange={e => setFilters(prev => ({ ...prev, search: e.target.value }))}
              />
            </div>
          </div>
          <div className="filter-field">
            <label className="filter-label">Estado</label>
            <select
              className="form-input"
              style={cancelledFilter ? { borderColor: 'var(--primary)', boxShadow: '0 0 0 1px var(--primary)' } : undefined}
              value={cancelledFilter}
              onChange={e => setFilters(prev => ({ ...prev, cancelledFilter: e.target.value }))}
            >
              <option value="">Todas</option>
              <option value="vigentes">Vigentes</option>
              <option value="anuladas">Anuladas</option>
            </select>
          </div>
          <div className="filter-field">
            <label className="filter-label">Cobro</label>
            <select
              className="form-input"
              style={collectionFilter ? { borderColor: 'var(--primary)', boxShadow: '0 0 0 1px var(--primary)' } : undefined}
              value={collectionFilter}
              onChange={e => setFilters(prev => ({ ...prev, collectionFilter: e.target.value }))}
            >
              <option value="">Todos</option>
              <option value="pendiente">Pendiente</option>
              <option value="parcial">En parte</option>
              <option value="cobrada">Cobrada</option>
              <option value="vencida">Vencida</option>
            </select>
          </div>
          <div className="filter-field">
            <label className="filter-label">Origen</label>
            <select
              className="form-input"
              style={originFilter ? { borderColor: 'var(--primary)', boxShadow: '0 0 0 1px var(--primary)' } : undefined}
              value={originFilter}
              onChange={e => setFilters(prev => ({ ...prev, originFilter: e.target.value }))}
            >
              <option value="">Todos</option>
              <option value="minerva">Minerva</option>
              <option value="manual">A mano</option>
            </select>
          </div>
          <div className="filter-field">
            <label className="filter-label">Envío al cliente</label>
            <select
              className="form-input"
              style={visibleFilter ? { borderColor: 'var(--primary)', boxShadow: '0 0 0 1px var(--primary)' } : undefined}
              value={visibleFilter}
              onChange={e => setFilters(prev => ({ ...prev, visibleFilter: e.target.value }))}
            >
              <option value="">Todas</option>
              <option value="si">Enviadas</option>
              <option value="no">Sin enviar</option>
            </select>
          </div>
          {!proformaMode && (
            <div className="filter-field">
              <label className="filter-label">Visibilidad</label>
              <select
                className="form-input"
                style={asesoriaFilter ? { borderColor: 'var(--primary)', boxShadow: '0 0 0 1px var(--primary)' } : undefined}
                value={asesoriaFilter}
                onChange={e => setFilters(prev => ({ ...prev, asesoriaFilter: e.target.value }))}
                title="Visible para la asesoría"
              >
                <option value="">Todas</option>
                <option value="si">Visible asesoría</option>
                <option value="no">No visible</option>
              </select>
            </div>
          )}
          <div className="filter-field">
            <label className="filter-label">Cierre</label>
            <select
              className="form-input"
              style={closedFilter ? { borderColor: 'var(--primary)', boxShadow: '0 0 0 1px var(--primary)' } : undefined}
              value={closedFilter}
              onChange={e => setFilters(prev => ({ ...prev, closedFilter: e.target.value }))}
            >
              <option value="">Todas</option>
              <option value="si">Cerrado</option>
              <option value="no">Abierto</option>
            </select>
          </div>
          {!proformaMode && (
            <div className="filter-field">
              <label className="filter-label">Contabilización</label>
              <select
                className="form-input"
                style={accountedFilter ? { borderColor: 'var(--primary)', boxShadow: '0 0 0 1px var(--primary)' } : undefined}
                value={accountedFilter}
                onChange={e => setFilters(prev => ({ ...prev, accountedFilter: e.target.value }))}
              >
                <option value="">Todas</option>
                <option value="si">Contabilizadas</option>
                <option value="no">Sin contabilizar</option>
              </select>
            </div>
          )}
          <div className="filter-field">
            <label className="filter-label">Cliente</label>
            <select
              className="form-input"
              style={customerFilter ? { borderColor: 'var(--primary)', boxShadow: '0 0 0 1px var(--primary)' } : undefined}
              value={customerFilter}
              onChange={e => setFilters(prev => ({ ...prev, customerFilter: e.target.value }))}
            >
              <option value="">Todos</option>
              {Array.from(new Map(invoices
                .filter(i => i.customer)
                .map(i => [i.customer.id, i.customer]))
                .values())
                .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
                .map(c => (
                  <option key={c.id} value={c.id}>#{c.id} {c.name}</option>
                ))}
            </select>
          </div>
          <div className="filter-field">
            <label className="filter-label">Orden</label>
            <select
              className="form-input"
              value={sortKey}
              onChange={e => setFilters(prev => ({ ...prev, sortKey: e.target.value }))}
            >
              <option value="date_desc">Más reciente</option>
              <option value="date_asc">Más antigua</option>
              <option value="number_desc">Nº ↓</option>
              <option value="number_asc">Nº ↑</option>
              <option value="customer_asc">Cliente A→Z</option>
              <option value="customer_desc">Cliente Z→A</option>
              <option value="amount_desc">Importe ↓</option>
              <option value="amount_asc">Importe ↑</option>
            </select>
          </div>
          {activeFilterCount > 0 && (
            <div className="filter-field">
              <label className="filter-label" style={{ visibility: 'hidden' }}>·</label>
              <span style={{
                fontSize: 12, fontWeight: 700, color: 'var(--primary)',
                background: 'rgba(59,130,246,0.10)', padding: '9px 12px', borderRadius: 8, whiteSpace: 'nowrap',
              }}>{activeFilterCount} {activeFilterCount === 1 ? 'filtro' : 'filtros'}</span>
            </div>
          )}
          <div className="filter-field" style={{ marginLeft: 'auto' }}>
            <label className="filter-label" style={{ visibility: 'hidden' }}>·</label>
            <VistaControles
              viewMode={viewMode}
              onViewModeChange={setViewMode}
              fields={fields}
              hidden={fl.hidden}
              onToggleField={fl.toggle}
              onReorderFields={fl.reorder}
            />
          </div>
        </div>

        {!loading && filtered.length > 0 && (
          <div style={{
            display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'center',
            padding: '10px 14px', margin: '8px 0 12px', background: '#f8fafc',
            border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 13,
          }}>
            <span style={{ color: '#64748b', fontWeight: 600 }}>Totales ({filtered.length}):</span>
            <span><span style={{ color: '#64748b' }}>Base imponible</span> <strong style={{ marginLeft: 4 }}>{formatCurrency(totals.base)}</strong></span>
            <span><span style={{ color: '#64748b' }}>IVA</span> <strong style={{ marginLeft: 4 }}>{formatCurrency(totals.iva)}</strong></span>
            <span><span style={{ color: '#64748b' }}>IRPF</span> <strong style={{ marginLeft: 4, color: totals.irpf > 0 ? '#b45309' : 'inherit' }}>{formatCurrency(-totals.irpf)}</strong></span>
            <span style={{ marginLeft: 'auto' }}><span style={{ color: '#64748b' }}>Total</span> <strong style={{ marginLeft: 4, fontSize: 15 }}>{formatCurrency(totals.total)}</strong></span>
          </div>
        )}

        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#64748b' }}>Cargando...</div>
        ) : filtered.length === 0 ? (
          <div className="empty-state">
            <FileText size={64} />
            <h3>No hay facturas</h3>
            <p>Crea tu primera factura para empezar</p>
          </div>
        ) : (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
              {hasPermission('invoices', 'edit') && (
                <AccionesSeleccion
                  count={selectedShownCount}
                  scope={quarter === 'all' ? `de ${year}` : `del T${quarter} de ${year}`}
                  disabled={bulkRunning}
                  actions={accionesSeleccion}
                />
              )}
              <input
                type="checkbox"
                checked={allFilteredSelected}
                onChange={toggleSelectAllFiltered}
                disabled={selectableFilteredIds.length === 0}
                title="Seleccionar todas las visibles"
              />
              <span className="item-card-note">
                Seleccionar todas las visibles ({selectableFilteredIds.length}){selectedShownCount > 0 ? ` · ${selectedShownCount} seleccionada${selectedShownCount === 1 ? '' : 's'}` : ''}
              </span>
              <input
                type="checkbox"
                checked={allTrimestreSelected}
                onChange={toggleSelectAllTrimestre}
                disabled={idsDelTrimestre.length === 0}
                title="Seleccionar TODAS las facturas del trimestre (por su fecha), aparezcan o no en el filtro. Solo selecciona; no cambia estados."
              />
              <span className="item-card-note">
                Seleccionar todas del {quarter === 'all' ? 'año' : `trimestre T${quarter}`} ({idsDelTrimestre.length})
              </span>
            </div>
            {viewMode === 'table' ? (
              <DataTable hiddenKeys={fl.hidden} columns={fl.orderColumns(invoiceColumns)} rows={sortedFiltered} segundaFila={['actions', 'collection', 'states']} getRowProps={filaConColor} />
            ) : (
            <><div className="items-grid">
            {sortedFiltered.map(invoice => (
              <div
                key={invoice.id}
                className="item-card"
                style={{ background: cardBgFromState(invoice), borderColor: cardBorderFromState(invoice) }}
              >
                <div className="item-card-header">
                  <div className="item-card-main">
                    <div className="item-card-check" onClick={e => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={selectedSet.has(invoice.id)}
                        onChange={e => toggleSelectOne(invoice.id, e.target.checked)}
                        title="Seleccionar factura"
                      />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="item-card-num" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      #{invoice.invoiceNumberSeq}
                      {invoice.isProforma && (
                        <span
                          title={invoice.convertedAt
                            ? 'Proforma ya convertida en factura (bloqueada)'
                            : 'Proforma: sin validez fiscal, numeración independiente. Editable hasta convertirla en factura.'}
                          style={{
                            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                            padding: '2px 6px', borderRadius: 4,
                            background: invoice.convertedAt ? '#94a3b8' : '#2563eb', color: '#fff',
                            fontSize: 10, fontWeight: 700, cursor: 'help', lineHeight: 1,
                          }}
                        >{invoice.convertedAt ? 'PRO→F' : 'PROFORMA'}</span>
                      )}
                      {invoice.isRectificativa && (
                        <span
                          title={`Factura rectificativa ${invoice.invoiceNumber}`}
                          style={{
                            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                            padding: '2px 6px', borderRadius: 4,
                            background: 'var(--warning)', color: 'var(--secondary)',
                            fontSize: 10, fontWeight: 700, cursor: 'help', lineHeight: 1,
                          }}
                        >RECTIF.</span>
                      )}
                      {invoice.createdByMinerva && (
                        <span
                          title="Creada por Minerva"
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: 22,
                            height: 22,
                            borderRadius: '50%',
                            background: '#7c3aed',
                            color: '#fff',
                            fontSize: 12,
                            fontWeight: 700,
                            cursor: 'help',
                            lineHeight: 1
                          }}
                        >M</span>
                      )}
                      {(() => {
                        const v = invoice.verifactuStatus;
                        // Solo mostrar el badge si la factura ha entrado realmente en el flujo
                        // VeriFactu (hay sent_at, o el estado denota un envío posterior al
                        // valor por defecto del schema). El "pendiente" de fábrica no cuenta.
                        const enFlujo =
                          !!invoice.verifactuSentAt ||
                          invoice.verifactuQueued ||
                          ['aceptado', 'aceptado_con_errores', 'rechazado', 'error_red', 'firmado'].includes(v);
                        if (!enFlujo) return null;
                        const map = {
                          pendiente:             { bg: '#fde68a', fg: '#92400e', tip: 'VeriFactu: en cola',                  txt: 'VF…' },
                          firmado:               { bg: '#fde68a', fg: '#92400e', tip: 'VeriFactu: enviando…',               txt: 'VF…' },
                          aceptado:              { bg: '#16a34a', fg: '#ffffff', tip: 'VeriFactu: aceptado por la AEAT',    txt: 'VF✓' },
                          aceptado_con_errores:  { bg: '#f59e0b', fg: '#ffffff', tip: 'VeriFactu: aceptado con errores',    txt: 'VF±' },
                          rechazado:             { bg: '#dc2626', fg: '#ffffff', tip: 'VeriFactu: rechazado por la AEAT',   txt: 'VF✗' },
                          error_red:             { bg: '#94a3b8', fg: '#ffffff', tip: 'VeriFactu: error de red, reintentando', txt: 'VF…' },
                        };
                        const m = map[v];
                        if (!m) return null;
                        return (
                          <span
                            title={m.tip}
                            style={{
                              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                              padding: '2px 6px', borderRadius: 4,
                              background: m.bg, color: m.fg,
                              fontSize: 10, fontWeight: 700, cursor: 'help', lineHeight: 1,
                            }}
                          >{m.txt}</span>
                        );
                      })()}
                    </div>
                    <div className="item-card-subtitle">{invoice.customer ? `#${invoice.customer.id} ${invoice.customer.name}` : ''}</div>
                    </div>
                  </div>
                  <div className="item-card-amount" style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2 }}>
                    <span title="Base imponible">{formatCurrency(parseFloat(invoice.subtotal))}</span>
                    <span style={{ fontSize: '0.72em', color: '#64748b', fontWeight: 500 }} title="Total factura">
                      Tot. {formatCurrency(parseFloat(invoice.total))}
                    </span>
                  </div>
                </div>
                <div className="item-card-body">
                  {!fl.isHidden('date') && (
                  <div className="item-card-row" style={fl.cardStyle('date')}>
                    <span className="item-label">Emisión</span>
                    <span>{formatDate(invoice.date)}</span>
                  </div>
                  )}
                  {!fl.isHidden('dueDate') && (
                  <div className="item-card-row" style={fl.cardStyle('dueDate')}>
                    <span className="item-label">Vencimiento</span>
                    <span style={{ color: isOverdue(invoice) ? '#dc2626' : 'inherit', fontWeight: isOverdue(invoice) ? 600 : 400 }}>
                      {formatDate(invoice.dueDate)}{isOverdue(invoice) ? ' · vencida' : ''}
                    </span>
                  </div>
                  )}
                  {!fl.isHidden('origin') && (
                  <div className="item-card-row" style={fl.cardStyle('origin')}>
                    <span className="item-label">Origen</span>
                    <span>{invoice.createdByMinerva ? 'Minerva' : 'A mano'}</span>
                  </div>
                  )}
                  {!fl.isHidden('states') && (
                  <div className="item-card-row" style={{ ...fl.cardStyle('states'), alignItems: 'flex-start', flexDirection: 'column', gap: 6 }}>
                    <span className="item-label" style={{ alignSelf: 'flex-start' }}>Estados</span>
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                      gap: 6,
                      width: '100%',
                    }}>
                      {/* Enviada al cliente */}
                      {invoice.sentToCustomerAt ? (
                        <span
                          className="badge badge-success"
                          onClick={() => hasPermission('invoices', 'edit') && handleQuickFlag(invoice.id, 'sentToCustomerAt', null)}
                          title={`Enviada al cliente el ${formatDateTime(invoice.sentToCustomerAt)} · clic para desmarcar`}
                          style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4, cursor: 'pointer', width: '100%' }}
                        >
                          <Send size={12} /> Enviada · {formatDate(invoice.sentToCustomerAt)}
                        </span>
                      ) : (
                        <span
                          className="badge badge-gray"
                          onClick={() => hasPermission('invoices', 'edit') && handleQuickFlag(invoice.id, 'sentToCustomerAt', new Date().toISOString())}
                          title="No enviada al cliente · clic para marcar como enviada"
                          style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4, cursor: 'pointer', width: '100%' }}
                        >
                          <Send size={12} /> Sin enviar
                        </span>
                      )}

                      {/* Estado del trimestre — SOLO INFORMATIVO. El cierre se gestiona
                          a nivel de trimestre, no factura por factura: una factura no se
                          cambia de trimestre desde aquí. */}
                      {invoice.quarterClosedAt ? (
                        <span
                          className="badge badge-info"
                          title={`Trimestre cerrado (entregado a la asesoría) el ${formatDateTime(invoice.quarterClosedAt)}`}
                          style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4, width: '100%' }}
                        >
                          <CalendarCheck2 size={12} /> Trim. cerrado
                        </span>
                      ) : (
                        <span
                          className="badge badge-gray"
                          title="Trimestre abierto (aún no entregado a la asesoría)"
                          style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4, width: '100%' }}
                        >
                          <CalendarCheck2 size={12} /> Trim. abierto
                        </span>
                      )}

                      {/* Contabilizada — la confirma la asesoría; clic para marcar/desmarcar.
                          Se refleja en el portal de asesoría. En Proformas no sale. */}
                      {!invoice.isProforma && (invoice.isAccounted ? (
                        <span
                          className="badge badge-info"
                          onClick={() => hasPermission('invoices', 'edit') && !invoice.cancelledAt && toggleAccounted(invoice.id, false)}
                          title="Contabilizada (confirmada por la asesoría) · clic para desmarcar"
                          style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4, cursor: invoice.cancelledAt ? 'default' : 'pointer', width: '100%' }}
                        >
                          <FileCheck size={12} /> Contabilizada
                        </span>
                      ) : (
                        <span
                          className="badge badge-gray"
                          onClick={() => hasPermission('invoices', 'edit') && !invoice.cancelledAt && toggleAccounted(invoice.id, true)}
                          title="Sin contabilizar · clic para marcar como contabilizada"
                          style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4, cursor: invoice.cancelledAt ? 'default' : 'pointer', width: '100%' }}
                        >
                          <FileCheck size={12} /> Sin contabilizar
                        </span>
                      ))}

                      {/* Cobrada (selector) */}
                      {invoice.cancelledAt ? (
                        <span className="badge badge-danger" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4, width: '100%' }}>
                          <XCircle size={12} /> Anulada
                        </span>
                      ) : (
                        <select
                          className={`badge ${collectionBadgeClass(invoice)}`}
                          value={invoice.collectionStatus || 'pendiente'}
                          onChange={e => handleCollectionChange(invoice.id, e.target.value)}
                          style={{ border: 'none', cursor: 'pointer', fontSize: '0.75rem', fontWeight: 600, paddingRight: 4, width: '100%', textAlign: 'center', textAlignLast: 'center' }}
                          title={isOverdue(invoice) ? 'Vencida' : ''}
                        >
                          {['pendiente', 'parcial', 'cobrada'].map(c => (
                            <option key={c} value={c}>{collectionLabels[c]}</option>
                          ))}
                        </select>
                      )}

                      {/* Visible asesoría: marca propia, independiente de «Enviada al
                          cliente»; decide qué ve la asesoría en su portal. Una
                          proforma nunca se enseña (su hueco es el de «Convertir»). */}
                      {!invoice.isProforma && (invoice.sentToAccountantAt ? (
                        <span
                          className="badge badge-success"
                          onClick={() => hasPermission('invoices', 'edit') && handleToggleAsesoria(invoice)}
                          title={`Visible para la asesoría desde el ${formatDateTime(invoice.sentToAccountantAt)} · clic para ocultar`}
                          style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4, cursor: hasPermission('invoices', 'edit') ? 'pointer' : 'default', width: '100%' }}
                        >
                          <Check size={12} /> Visible asesoría
                        </span>
                      ) : (
                        <span
                          className="badge badge-gray"
                          onClick={() => hasPermission('invoices', 'edit') && handleToggleAsesoria(invoice)}
                          title="No visible para la asesoría · clic para hacer visible"
                          style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4, cursor: hasPermission('invoices', 'edit') ? 'pointer' : 'default', width: '100%' }}
                        >
                          <Check size={12} /> No visible
                        </span>
                      ))}

                      {/* Proforma: botón claro para convertir en factura real (ocupa
                          el hueco del antiguo "override", que se eliminó por inservible) */}
                      {invoice.isProforma && !invoice.convertedAt && !invoice.cancelledAt && hasPermission('invoices', 'create') ? (
                        <button
                          type="button"
                          className="badge badge-info"
                          onClick={() => handleConvertProforma(invoice)}
                          title="Convertir esta proforma en factura real (número legal, emisión y VeriFactu si está activo). Pide confirmación."
                          style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4, width: '100%', cursor: 'pointer', border: 'none', fontWeight: 600 }}
                        >
                          <FileCheck size={12} /> Convertir en factura
                        </button>
                      ) : null}
                    </div>
                  </div>
                  )}
                  {invoice.cancelledAt && (
                    <div className="item-card-row">
                      <span className="item-label">Anulada</span>
                      <span className="badge badge-danger" style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                        <XCircle size={12} /> {formatDate(invoice.cancelledAt)}
                      </span>
                    </div>
                  )}
                  <div className="invoice-card-email">
                    <label className="invoice-card-email-label" htmlFor={`invoice-email-${invoice.id}`}>
                      Email cliente
                    </label>
                    <div className="invoice-card-email-row">
                      <input
                        id={`invoice-email-${invoice.id}`}
                        type="email"
                        className="form-input"
                        placeholder="cliente@ejemplo.com"
                        value={emailDrafts[invoice.id] ?? ''}
                        onChange={(e) => setEmailDrafts((prev) => ({ ...prev, [invoice.id]: e.target.value }))}
                      />
                      <button
                        type="button"
                        className="btn btn-outline invoice-card-email-button"
                        onClick={() => handleSendEmail(invoice)}
                        disabled={sendingEmailIds.includes(invoice.id)}
                      >
                        <Send size={16} />
                        {sendingEmailIds.includes(invoice.id) ? 'Enviando...' : 'Enviar'}
                      </button>
                    </div>
                  </div>
                </div>
                <div className="item-card-footer">
                  <div className="item-card-actions">
                    <button className="btn-icon" onClick={() => handleViewPdf(invoice.id)} title="Ver PDF">
                      <Eye size={16} />
                    </button>
                    <button className="btn-icon" onClick={() => handleDownloadPdf(invoice.id)} title="Descargar PDF">
                      <Download size={16} />
                    </button>
                    <button className="btn-icon" onClick={() => navigate(`/invoices/edit/${invoice.id}`)} title="Editar">
                      <Pencil size={16} />
                    </button>
                    {hasPermission('invoices', 'create') && (
                      <button className="btn-icon" onClick={() => openDuplicateDialog(invoice)} title="Duplicar factura">
                        <Copy size={16} />
                      </button>
                    )}
                    {hasPermission('invoices', 'edit') && (
                      <button
                        type="button"
                        className="btn-icon"
                        disabled={!anulacion(invoice).activo}
                        onClick={() => setInvoiceToCancel(invoice.id)}
                        title={anulacion(invoice).title}
                        style={{ color: anulacion(invoice).activo ? '#b45309' : undefined }}
                      >
                        <XCircle size={16} />
                      </button>
                    )}
                    {hasPermission('invoices', 'edit') && (
                      <button
                        type="button"
                        className="btn-icon"
                        disabled={!reenvioVerifactu(invoice).activo}
                        onClick={async () => {
                          try {
                            await api.post(`/invoices/${invoice.id}/verifactu/retry`);
                            await loadInvoices();
                            modal.success('Encolado a VeriFactu. En 30 s aparece el resultado en el badge.');
                          } catch (e) {
                            modal.error(e.response?.data?.message || 'No se pudo encolar a VeriFactu');
                          }
                        }}
                        title={reenvioVerifactu(invoice).title}
                        style={{ color: reenvioVerifactu(invoice).activo ? '#1d4ed8' : undefined }}
                      >
                        <RefreshCw size={16} />
                      </button>
                    )}
                    {!proformaMode && hasPermission('invoices', 'create') && (
                      <button
                        type="button"
                        className="btn-icon"
                        disabled={!rectificacion(invoice).activo}
                        onClick={() => navigate(`/invoices/new?rectifica=${invoice.id}`)}
                        title={rectificacion(invoice).title}
                      >
                        <FileEdit size={16} />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
            </div>
            </>
            )}
          </div>
        )}
      </div>

      {/* Leyenda de colores en un pie de una sola línea, fijo abajo, en tabla
          y en tarjetas; el hueco evita que tape la última factura. */}
      {colorLegend.length > 0 && (
        <>
          <div className="pie-leyenda-hueco" />
          <div className="pie-leyenda" title={`Colores: ${colorLegend.map((it) => it.labels).join(', ')}`}>
            <span className="pie-leyenda-titulo">Colores:</span>
            {colorLegend.map((it, i) => (
              <span key={i} className="pie-leyenda-item">
                <span className="pie-leyenda-muestra" style={{ background: it.bg, borderColor: it.border }} />
                {it.labels}
              </span>
            ))}
          </div>
        </>
      )}

      {cierre && (
        <CierreTrimestreModal
          grupos={cierre.grupos}
          documento={proformaMode ? { uno: 'proforma', varios: 'proformas' } : { uno: 'factura', varios: 'facturas' }}
          nota={proformaMode ? 'El cierre solo se puede deshacer desde Ajustes.' : 'Cuando todas las facturas de un trimestre quedan cerradas, sus proformas se cierran con ellas. El cierre solo se puede deshacer desde Ajustes.'}
          ocupado={bulkRunning}
          onCancel={() => setCierre(null)}
          onConfirm={confirmarCierre}
        />
      )}

      {invoiceToCancel !== null && (
        <div className="modal-overlay" onClick={() => setInvoiceToCancel(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Anular factura</h2>
              <button className="btn-icon" onClick={() => setInvoiceToCancel(null)}>&times;</button>
            </div>
            <div className="modal-body">
              <p>¿Seguro que quieres anular esta factura? Quedará marcada como anulada y no podrá cobrarse.</p>
              <p style={{ marginTop: 10, color: 'var(--gray-600)' }}>
                Anular es solo para una factura hecha por error, cuya operación no existió (duplicada, a otro cliente…). Se comunica a Hacienda por VeriFactu y no tiene vuelta atrás. Si la operación fue real y hay que corregir algo, no la anules: haz una rectificativa.
              </p>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-outline" onClick={() => setInvoiceToCancel(null)}>Cancelar</button>
              <button type="button" className="btn btn-danger" onClick={() => handleCancelInvoice(invoiceToCancel)}>Anular</button>
            </div>
          </div>
        </div>
      )}

      {sendDialog && (() => {
        // Estado VeriFactu EN VIVO de la factura del diálogo: se actualiza con el
        // sondeo del listado (refresca `invoices`), así el botón se desbloquea
        // solo cuando la AEAT acepta, sin recargar.
        const live = invoices.find((i) => i.id === sendDialog.invoice.id) || sendDialog.invoice;
        const vfStatus = live.verifactuStatus;
        const vfAceptado = ['aceptado', 'aceptado_con_errores'].includes(vfStatus);
        const vfRechazado = vfStatus === 'rechazado';
        const vfEnCurso = !!live.verifactuQueued && !vfAceptado && !vfRechazado;
        const enviando = sendingEmailIds.includes(sendDialog.invoice.id);
        const bloqueadoPorVf = vfEnCurso && !sendForceAllowed;
        const puedeEnviar = !enviando && !!sendDialog.recipient && !vfRechazado && !bloqueadoPorVf;
        const textoBoton = enviando ? 'Enviando...'
          : bloqueadoPorVf ? 'Esperando a VeriFactu…'
          : (vfEnCurso && sendForceAllowed) ? 'Enviar de todas formas'
          : 'Confirmar y enviar';
        return (
        <div className="modal-overlay" onClick={() => !sendingEmailIds.includes(sendDialog.invoice.id) && setSendDialog(null)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 540 }}>
            <div className="modal-header">
              <h2>Enviar factura {sendDialog.preview?.invoiceNumber || sendDialog.invoice.invoiceNumber} por email</h2>
              <button className="btn-icon" onClick={() => setSendDialog(null)} disabled={sendingEmailIds.includes(sendDialog.invoice.id)}>&times;</button>
            </div>
            <div className="modal-body">
              <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', rowGap: 10, columnGap: 12, fontSize: 14 }}>
                <strong>Remitente:</strong>
                <span>
                  {sendDialog.preview?.senderName ? `${sendDialog.preview.senderName} ` : ''}
                  &lt;{sendDialog.preview?.senderEmail || '—'}&gt;
                </span>
                <strong>Destinatario:</strong>
                <input
                  type="email"
                  className="form-input"
                  value={sendDialog.recipient}
                  onChange={e => setSendDialog(prev => ({ ...prev, recipient: e.target.value }))}
                  disabled={sendingEmailIds.includes(sendDialog.invoice.id)}
                />
                <strong>Adjunto:</strong>
                <span>📎 {sendDialog.preview?.attachmentFileName || '—'} (PDF)</span>
                <strong>Cuerpo:</strong>
                <span style={{ color: '#666' }}>HTML con saludo, total y datos del documento. El PDF va como archivo adjunto, NO como link.</span>
                {sendDialog.preview?.lastSentToCustomerAt && (
                  <>
                    <strong>Último envío:</strong>
                    <span style={{ color: '#666' }}>{new Date(sendDialog.preview.lastSentToCustomerAt).toLocaleString('es-ES')}</span>
                  </>
                )}
              </div>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 18, fontSize: 14, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={sendDialog.sendCopy}
                  onChange={e => setSendDialog(prev => ({ ...prev, sendCopy: e.target.checked }))}
                  disabled={sendingEmailIds.includes(sendDialog.invoice.id)}
                />
                Enviarme también una copia (CCO al remitente)
              </label>
              {vfEnCurso && (
                <div style={{ marginTop: 14, fontSize: 13, color: '#666' }}>
                  ⏳ Esperando la confirmación de <strong>VeriFactu</strong> (AEAT). El botón de enviar se activa en cuanto la factura quede registrada (suele tardar unos segundos).
                  {sendForceAllowed && ' Está tardando más de lo normal: puedes enviarla igualmente, la factura ya es válida con su QR y el registro en la AEAT se reintenta solo.'}
                </div>
              )}
              {vfRechazado && (
                <div style={{ marginTop: 14, fontSize: 13, color: '#dc2626', fontWeight: 600 }}>
                  VeriFactu ha RECHAZADO esta factura{live.verifactuResponse ? `: ${live.verifactuResponse}` : ''}. No se puede enviar al cliente hasta corregirla y reenviarla a VeriFactu.
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-outline" onClick={() => setSendDialog(null)} disabled={sendingEmailIds.includes(sendDialog.invoice.id)}>Cancelar</button>
              <button type="button" className="btn btn-primary" onClick={handleConfirmSend} disabled={!puedeEnviar}>
                {textoBoton}
              </button>
            </div>
          </div>
        </div>
        );
      })()}

      {invoiceToDuplicate !== null && (
        <div className="modal-overlay" onClick={closeDuplicateDialog}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 560 }}>
            <div className="modal-header">
              <h2>Duplicar factura {invoiceToDuplicate.invoiceNumber}</h2>
              <button className="btn-icon" onClick={closeDuplicateDialog} disabled={duplicating}>&times;</button>
            </div>
            <div className="modal-body">
              <p style={{ marginTop: 0 }}>
                Se creará una factura nueva como <strong>borrador</strong>, con número correlativo de la misma serie, fecha de hoy, mismo cliente y mismas líneas. El plazo de pago se conserva.
              </p>
              <div style={{ marginTop: 16 }}>
                <label style={{ display: 'block', fontWeight: 600, marginBottom: 8 }}>
                  Sustituciones de texto en las líneas (opcional)
                </label>
                <p style={{ fontSize: 13, color: '#666', marginTop: 0, marginBottom: 12 }}>
                  Cada sustitución se aplica a TODAS las descripciones de líneas. Útil para "segunda parte" → "tercera parte".
                </p>
                {duplicateReplacements.map((r, i) => (
                  <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: 8, marginBottom: 8 }}>
                    <input
                      type="text"
                      placeholder="Texto a buscar"
                      value={r.from}
                      onChange={e => updateDuplicateReplacement(i, 'from', e.target.value)}
                      disabled={duplicating}
                    />
                    <input
                      type="text"
                      placeholder="Texto que lo sustituye"
                      value={r.to}
                      onChange={e => updateDuplicateReplacement(i, 'to', e.target.value)}
                      disabled={duplicating}
                    />
                    <button
                      type="button"
                      className="btn-icon"
                      onClick={() => removeDuplicateReplacement(i)}
                      disabled={duplicating}
                      title="Quitar"
                      style={{ color: '#ef4444' }}
                    >
                      <XCircle size={16} />
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  className="btn btn-outline"
                  onClick={addDuplicateReplacement}
                  disabled={duplicating}
                  style={{ marginTop: 4 }}
                >
                  <Plus size={14} /> Añadir sustitución
                </button>
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-outline" onClick={closeDuplicateDialog} disabled={duplicating}>Cancelar</button>
              <button type="button" className="btn btn-primary" onClick={handleDuplicateInvoice} disabled={duplicating}>
                {duplicating ? 'Duplicando...' : 'Duplicar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Invoices;
